import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { batch } from 'react-redux';
import { store } from 'src/common/redux/configure-store';
import { SalesKeypadActions } from 'src/common/redux/reducers/sales-keypad-reducer';
import { UnitOfMeasureTypes, UomText } from '../../common/enumerations/unit-of-measure-type';
import { IButton } from '../../common/interfaces/IButton';
import Keypad from '../../keypad/components/keypad';
import {
  onSalesKeypadSubmit,
  resetSalesKeypadModifiers,
} from '../business-logic/handle-sales-keypad-actions';

type Props = ISalesKeypadState;
const DEFAULT_MW = false;
const DEFAULT_METRIC_VALUE = 1;
const DEFAULT_TYPE = UnitOfMeasureTypes.Quantity;

const SalesKeypad: React.FC<Props> = (props: Props): JSX.Element | null => {
  const {
    keyboardId,
    isKeypadVisible,
    keyPadSubmit,
    toggleKeypad,
    disableInputField,
    disableManualWeight,
    disableQuantity,
    disableDecimalInput,
    keyPadSubmitLabel,
    disableSubmitWhenNoInput,
    topLabelText,
    inputInstructionsLabelText,
    disableInstructionsLabel,
    initialInputValue,
    style,
    resetKeypadToggle,
    handleOnInputChange,
  } = props;

  const [isManualWeight, setManualWeight] = useState<boolean>(DEFAULT_MW);
  const [metricValue, setMetricValue] = useState<number>(DEFAULT_METRIC_VALUE);
  const [type, setType] = useState<UnitOfMeasureTypes>(DEFAULT_TYPE);
  const [quantityEntered, setQuantityEntered] = useState<boolean>(false);

  useEffect(() => {
    batch(() => {
      store.dispatch(
        SalesKeypadActions.setSalesKeypadState({
          isManualWeight,
          metricValue,
          type,
          quantityEntered,
        }),
      );
    });
  }, [isManualWeight, metricValue, type, quantityEntered]);

  const keypadModifiers: ISalesKeypadModifiers = {
    isManualWeight: isManualWeight,
    metricValue: metricValue,
    type: type,
    isQuantityEntered: quantityEntered,
    setManualWeight: setManualWeight,
    setMetricValue: setMetricValue,
    setType: setType,
    setQuantityEntered: setQuantityEntered,
  };

  const resetSalesKeypadLocalState = () => {
    setManualWeight(DEFAULT_MW);
    setMetricValue(DEFAULT_METRIC_VALUE);
    setType(DEFAULT_TYPE);
  };

  useEffect(() => {
    resetSalesKeypadLocalState();
  }, [resetKeypadToggle]);

  const dataTestIdModifier = props.dataTestIdModifier ? `-${props.dataTestIdModifier}` : '';

  const keypadSubmitButton: IButton = {
    label: keyPadSubmitLabel ? keyPadSubmitLabel : 'OK',
    onClick: keyPadSubmit
      ? (input: string) => {
          keyPadSubmit(input, keypadModifiers);
          resetSalesKeypadModifiers(keypadModifiers);
        }
      : (input: string) => {
          onSalesKeypadSubmit(input);
          resetSalesKeypadModifiers(keypadModifiers);
        },
  };

  const { t } = useTranslation();
  const SALES_KEYPAD_TOP_LABEL: string = t('Keypad_Label_Sale_Mode');
  let salesKeypadInputInstructions = '';
  let keypadLabelText = '';
  let inputFieldText = '';

  if (!disableInstructionsLabel) {
    keypadLabelText = topLabelText ? topLabelText : SALES_KEYPAD_TOP_LABEL;
    salesKeypadInputInstructions = inputInstructionsLabelText
      ? inputInstructionsLabelText
      : `${t('RecallHelperText')}`;
    inputFieldText = isManualWeight
      ? `${metricValue} ${UomText[type]}`
      : `${UomText[type]}  ${metricValue}`;
  }

  return (
    <Keypad
      salesKeypadModifiers={keypadModifiers}
      keyboardId={`shared-sales-${keyboardId}`}
      visible={isKeypadVisible}
      topLabelText={keypadLabelText}
      inputInstructionsLabelText={salesKeypadInputInstructions}
      inputFieldText={inputFieldText}
      initialInputValue={initialInputValue}
      submitButton={keypadSubmitButton}
      keypadTestIdPrefix={`shared-sales${dataTestIdModifier}-`}
      handleClose={toggleKeypad}
      noInputField={disableInputField}
      disableManualWeight={disableManualWeight}
      disableQuantity={disableQuantity}
      disableSubmitWhenNoInput={disableSubmitWhenNoInput}
      disableDecimalInput={disableDecimalInput}
      style={style}
      handleOnInputChange={handleOnInputChange}
    />
  );
};

interface ISalesKeypadState {
  keyboardId: string;
  isKeypadVisible?: boolean;
  toggleKeypad?: () => void;
  dataTestIdModifier?: string;
  keyPadSubmit?: (input: string, salesKeypadModifiers: ISalesKeypadModifiers) => void;
  keyPadSubmitLabel?: string;
  initialInputValue?: string;
  inputInstructionsLabelText?: string;
  disableInputField?: boolean;
  disableManualWeight?: boolean;
  disableQuantity?: boolean;
  disableDecimalInput?: boolean;
  disableSubmitWhenNoInput?: boolean;
  disableInstructionsLabel?: boolean;
  topLabelText?: string;
  style?: React.CSSProperties;
  resetKeypadToggle?: boolean;
  handleOnInputChange?: (inputValue: string) => void;
}

export interface ISalesKeypadModifiers {
  isManualWeight: boolean;
  metricValue: number;
  type: string;
  isQuantityEntered: boolean;
  setManualWeight?: Function;
  setMetricValue?: Function;
  setType?: Function;
  setQuantityEntered: Function;
}

export default React.memo(SalesKeypad);
